import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../../context/PermissionContext';
import { getInitialPage } from '../HelperFunctions/atoms';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const userData = localStorage.getItem('userData');
  const { hasAnyPermission, isSubAdmin, permissions } = usePermissions();
  let isAuthenticated = false;
  let userType = '';

  try {
    if (userData) {
      const parsedUserData = JSON.parse(userData);
      isAuthenticated = parsedUserData?.isAuthenticated || false;
      userType = parsedUserData?.userType || '';
    }
  } catch (error) {
    console.error('Error parsing user data:', error);
    isAuthenticated = false;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // For subadmins, check route permissions
  if (isSubAdmin && userType === 'subAdmin') {
    // Define route-to-permission mapping with path patterns
    const routePermissionMap: Array<{ pattern: string; permission: string }> = [
      { pattern: '/dashboard', permission: 'dashboard' },
      { pattern: '/manage-choice', permission: 'choices' },
      { pattern: '/manage-choice-group', permission: 'choiceGroup' },
      { pattern: '/managecuisine', permission: 'storeCategory' },
      { pattern: '/manageItemCategory', permission: 'itemCategory' },
      { pattern: '/manageItemSubCategory', permission: 'itemCategory' },
      { pattern: '/store-owner', permission: 'storeOwner' },
      { pattern: '/manage-store', permission: 'store' },
      { pattern: '/manageCustomer', permission: 'customer' },
      { pattern: '/add-customer', permission: 'customer' },
      { pattern: '/join-us', permission: 'joinUs' },
      { pattern: '/manageStoreItem', permission: 'item' },
      { pattern: '/addStoreItem', permission: 'item' },
      { pattern: '/admin-delivery', permission: 'deliveryManager' },
      { pattern: '/delivery-boy', permission: 'deliveryBoy' },
      { pattern: '/manageCoupon', permission: 'coupon' },
      { pattern: '/manage-CMS', permission: 'cms' },
      { pattern: '/manage-FAQ', permission: 'faq' },
      { pattern: '/manageOrder', permission: 'order' },
      { pattern: '/settlements', permission: 'settlement' },
      { pattern: '/manageStock', permission: 'inventory' },
      { pattern: '/manageWallet', permission: 'wallet' },
      { pattern: '/wallet-history', permission: 'wallet' },
      { pattern: '/push-notification', permission: 'pushNotification' },
      { pattern: '/Manage-Offers', permission: 'promotionalAndOffers' },
      { pattern: '/assign-offers', permission: 'promotionalAndOffers' },
      { pattern: '/reports', permission: 'reports' },
      { pattern: '/settings', permission: 'settings' },
      { pattern: '/bannerSettings', permission: 'settings' },
      { pattern: '/manage-item-review', permission: 'itemReview' },
      { pattern: '/manage-restaurant-review', permission: 'storeReview' },
      { pattern: '/manage-order-review', permission: 'deliveryReview' },
      { pattern: '/sub-admin', permission: 'subAdmin' },
      { pattern: '/addRestaurant', permission: 'store' },
      { pattern: '/restaurant/edit', permission: 'store' },
    ];

    const currentPath = location.pathname;
    
    // If accessing root path, redirect to appropriate page
    if (currentPath === '/') {
      const redirectPath = getInitialPage(userType, permissions);
      return <Navigate to={redirectPath} replace />;
    }
    
    // Find matching route pattern
    const matchingRoute = routePermissionMap.find(route => 
      currentPath.startsWith(route.pattern)
    );

    // If this route requires a specific permission and user doesn't have it
    if (matchingRoute && !hasAnyPermission(matchingRoute.permission)) {
      // Redirect to the first available page based on permissions
      const redirectPath = getInitialPage(userType, permissions);
      console.log(`Subadmin doesn't have permission for ${currentPath} (${matchingRoute.permission}), redirecting to ${redirectPath}`);
      return <Navigate to={redirectPath} replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
